/**
 * The "Started a video call" message rendered as a call card (TRUS-668):
 * "Video call · Active" + Join, or "Video call · Ended · 3m 12s".
 * Live state comes from GET /calls/{id} while the metadata says active.
 *
 * v2: "· Recording" while a recorded call runs; after it, "Notes are being
 * prepared…" while the notes pipeline runs, "Notes unavailable" if it failed
 * (both from metadata.call.notes_status, which the backend keeps current).
 *
 * 2026-10-22: an ended call says WHY it has no notes (staff only): "Not
 * recorded" + how to record, "Notes failed: <reason>", "Notes are off for this
 * organization"… (metadata.call.notes_reason, or GET /calls/{id} for a failure).
 *
 * TRUS-708: a call whose notes wait for configuration says so (not "being
 * prepared"), a failed call whose recording was deleted says it is gone, and
 * "Retry notes" shows when the backend says `notes_retryable`.
 *
 * v5: once the notes are `ready`, a View notes toggle expands the notes card
 * inline (staff only — members never see notes), so a call's notes are
 * reachable from the call itself even when no "Call notes" message was posted.
 */

import React, { useId, useState } from 'react';
import { ChevronDown, ChevronRight, Phone, PhoneOff, Video as VideoIcon } from 'lucide-react';
import { useAppTranslation } from '@/contexts/TranslationContext';
import { cn } from '@/lib/utils';
import { formatCallDuration, isNotesPending, type CallMessageMeta, type CallNotesReason } from '@/services/callsApi';
import { Button } from '../../ui/button';
import { callStore, useCallStore } from './callStore';
import { CallNotesCard } from './CallNotesCard';
import { callNotesReasonText, callNotesRecordingGoneText } from './callNotesReason';
import { CallNotesRetryButton } from './CallNotesRetryButton';
import { useCallsApi, useCallsConfig, useLiveCall } from './useCalls';

interface CallMessageCardProps {
  meta: CallMessageMeta;
  channelId: number;
  channelName?: string;
  /** Blue (own staff) bubble: light text. */
  tone?: 'default' | 'inverted';
}

export function CallMessageCard({ meta, channelId, channelName, tone = 'default' }: CallMessageCardProps) {
  const { t } = useAppTranslation();
  const api = useCallsApi();
  const { config } = useCallsConfig();
  const [notesOpen, setNotesOpen] = useState(false);
  const notesRegionId = useId();
  const { session } = useCallStore();
  // Notes states come from metadata.call (recording + notes_status). The thread
  // poll refreshes that metadata, but not everywhere a card is shown (a thread
  // panel, a message scrolled out of the fetched page), so staff also poll
  // GET /calls/{id} (every 15 s, useLiveCall) while an ended recorded call's
  // notes are pending — the card switches to View notes once they are ready.
  const notesMayArrive =
    api.kind === 'staff' && meta.status === 'ended' && meta.recording !== false && isNotesPending(meta.notes_status);
  // A failure's reason is not on the message (members can read its metadata):
  // staff fetch the call once (it stops polling: ended, not pending).
  const notesFailed = api.kind === 'staff' && meta.status === 'ended' && meta.notes_status === 'failed';
  const live = useLiveCall(meta.call_id, config.enabled && (meta.status === 'active' || notesMayArrive || notesFailed));
  const status = live.data?.status ?? meta.status;
  const mode = live.data?.mode ?? meta.mode;
  const duration = live.data?.duration_seconds ?? meta.duration_seconds ?? null;
  const inThisCall = session?.callId === meta.call_id;
  const isActive = status === 'active';
  const inverted = tone === 'inverted';
  const recording = live.data?.recording ?? meta.recording ?? false;
  const notesStatus = live.data?.notes_status ?? meta.notes_status;
  const consentNeeded = !!live.data?.recording && !live.data?.my_recording_consent;

  const modeLabel = mode === 'video' ? t('messaging.calls.videoCall', 'Video call') : t('messaging.calls.audioCall', 'Audio call');
  const missed = !isActive && /miss|declin|no_answer|unanswered/i.test(meta.ended_reason || '');
  const statusLabel = missed
    ? t('messaging.calls.missed', 'Missed')
    : isActive
    ? inThisCall
      ? t('messaging.calls.inCall', 'In call')
      : t('messaging.calls.active', 'Active')
    : duration != null && duration > 0
      ? t('messaging.calls.endedWithDuration', 'Ended · {{duration}}', { duration: formatCallDuration(duration) })
      : t('messaging.calls.ended', 'Ended');

  const Icon = !isActive ? PhoneOff : mode === 'video' ? VideoIcon : Phone;
  const canViewNotes = !isActive && notesStatus === 'ready' && api.kind === 'staff';
  // Why there are no notes (staff, ended, not missed). Messages from before the
  // backend stamped notes_reason fall back on the org's config.
  let notesReason: CallNotesReason | null = null;
  const waiting = !isActive && api.kind === 'staff' && live.data?.notes_reason === 'waiting_for_configuration';
  if (waiting) {
    notesReason = 'waiting_for_configuration';
  } else if (!isActive && !missed && api.kind === 'staff' && !isNotesPending(notesStatus) && notesStatus !== 'ready') {
    notesReason =
      notesStatus === 'failed'
        ? 'failed'
        : (live.data?.notes_reason ?? meta.notes_reason ?? (recording ? null : config.notes_enabled ? 'not_recorded' : 'notes_off'));
  }
  const why = callNotesReasonText(t, notesReason, waiting ? null : live.data?.notes_error);
  const recordingGone = !isActive && api.kind === 'staff' && notesStatus === 'failed' && live.data?.notes_media_deleted === true;
  const canRetry = !isActive && api.kind === 'staff' && live.data?.notes_retryable === true;

  return (
    <div className="flex flex-col gap-1" data-testid={`call-card-${meta.call_id}`} data-status={status}>
      <div className="flex items-center gap-3 py-1">
        <div
          className={cn(
            'flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full',
            isActive
              ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300'
              : inverted
                ? 'bg-white/20 text-white'
                : 'bg-slate-200 text-slate-600 dark:bg-slate-700 dark:text-slate-200',
          )}
        >
          <Icon className="h-4 w-4" />
        </div>
        <div className="min-w-0">
          <div className={cn('text-sm font-semibold', inverted && 'text-white')}>{modeLabel}</div>
          <div className={cn('text-xs', inverted ? 'text-white/80' : 'text-muted-foreground')} data-testid="call-card-status">
            {statusLabel}
            {isActive && recording && (
              <span className="ml-1 text-red-600 dark:text-red-400" data-testid="call-card-recording">
                {'· '}
                {t('messaging.calls.recording', 'Recording')}
              </span>
            )}
          </div>
          {/* Notes are staff-only (TRUS-690): a member's card never mentions them. */}
          {!isActive && api.kind === 'staff' && isNotesPending(notesStatus) && !waiting && (
            <div className={cn('text-xs italic', inverted ? 'text-white/80' : 'text-muted-foreground')} data-testid="call-card-notes">
              {t('messaging.calls.notesPreparing', 'Notes are being prepared…')}
            </div>
          )}
          {why && (
            <div className={cn('text-xs', inverted ? 'text-white/80' : 'text-muted-foreground')} data-testid="call-card-notes">
              <span data-testid="call-card-notes-reason" data-reason={notesReason ?? undefined}>
                {why.label}
              </span>
              {why.hint && (
                <span className="block italic" data-testid="call-card-notes-hint">
                  {why.hint}
                </span>
              )}
            </div>
          )}
          {recordingGone && (
            <div className={cn('text-xs italic', inverted ? 'text-white/80' : 'text-muted-foreground')} data-testid="call-card-recording-deleted">
              {callNotesRecordingGoneText(t)}
            </div>
          )}
          {canRetry && <CallNotesRetryButton callId={meta.call_id} tone={tone} className="mt-0.5" />}
          {canViewNotes && (
            <button
              type="button"
              className={cn(
                'mt-0.5 flex items-center gap-1 rounded-sm text-xs font-medium underline-offset-2 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                inverted ? 'text-white' : 'text-primary',
              )}
              onClick={() => setNotesOpen((v) => !v)}
              aria-expanded={notesOpen}
              aria-controls={notesRegionId}
              data-testid={`call-card-view-notes-${meta.call_id}`}
            >
              {notesOpen ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />}
              {notesOpen ? t('messaging.calls.hideNotes', 'Hide notes') : t('messaging.calls.viewNotes', 'View notes')}
            </button>
          )}
        </div>
        {isActive && config.enabled && !inThisCall && (
          <Button
            type="button"
            size="sm"
            className="ml-auto gap-1 bg-emerald-600 text-white hover:bg-emerald-700"
            onClick={() => callStore.open({ callId: meta.call_id, channelId, mode, channelName, consentNeeded })}
            data-testid={`call-card-join-${meta.call_id}`}
          >
            {mode === 'video' ? <VideoIcon className="h-3.5 w-3.5" /> : <Phone className="h-3.5 w-3.5" />}
            {t('messaging.calls.join', 'Join')}
          </Button>
        )}
      </div>
      {canViewNotes && notesOpen && (
        <div
          id={notesRegionId}
          role="region"
          aria-label={t('messaging.calls.notesTitle', 'Call notes')}
          className={cn('border-l-2 pl-3', inverted ? 'border-white/40' : 'border-border')}
          data-testid={`call-card-notes-panel-${meta.call_id}`}
        >
          <CallNotesCard meta={{ call_id: meta.call_id, notes_id: null }} tone={tone} showTitle={false} />
        </div>
      )}
    </div>
  );
}
